const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

export const SURECO_PLATFORM_FEE_LABEL = 'SureCo Platform Fee'

export const THE_CASON_GROUP_FEE_LABEL = 'The Cason Group Fee'

export const getBrokerLabel = (brokerName: string | null | undefined): string =>
  brokerName?.trim() || 'Broker'

export const getBrokerFeeLabel = (
  brokerName: string | null | undefined
): string => {
  const name = brokerName?.trim()
  return name ? `${name} Fee` : 'Broker Fee'
}

export const formatCurrency = (value: number): string =>
  currencyFormatter.format(value)

export const formatStatusLabel = (status?: string): string =>
  status
    ? status
        .toLowerCase()
        .split('_')
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ')
    : 'In Review'

// Keyed by the BillingMonth enum values used on line items (JAN, FEB, …), so the
// Health Plans Month column renders title-cased ("Jun 2026").
const MONTH_LABELS: Record<string, string> = {
  JAN: 'Jan',
  FEB: 'Feb',
  MAR: 'Mar',
  APR: 'Apr',
  MAY: 'May',
  JUN: 'Jun',
  JUL: 'Jul',
  AUG: 'Aug',
  SEP: 'Sep',
  OCT: 'Oct',
  NOV: 'Nov',
  DEC: 'Dec',
}

// Index → BillingMonth enum value, used to map the invoice's billing-period month
// number to the enum value that line items carry.
export const BILLING_MONTH_ORDER = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
] as const

export const formatBillingMonth = (
  billingMonth: string,
  billingYear: number
): string => {
  const label = MONTH_LABELS[billingMonth?.toUpperCase()] ?? billingMonth
  return `${label} ${billingYear}`
}

const MONTH_LABEL_INDEX = new Map(
  BILLING_MONTH_ORDER.map((key, index) => [MONTH_LABELS[key], index])
)

/**
 * Sort key for a "MMM YYYY" month label so month columns order chronologically
 * instead of alphabetically (Apr before Jun, not Apr before Feb). Labels that
 * carry no month — "Month not specified" year-only corrections — sort last.
 */
export const billingMonthLabelSortKey = (label?: string | null): number => {
  if (!label) return Number.MAX_SAFE_INTEGER
  const [month, year] = label.split(' ')
  const monthIndex = MONTH_LABEL_INDEX.get(month)
  const parsedYear = Number(year)
  if (monthIndex === undefined || !Number.isFinite(parsedYear)) {
    return Number.MAX_SAFE_INTEGER
  }
  return parsedYear * 12 + monthIndex
}

// Amount without the currency symbol; the "$" sits in its own column to mirror
// the funding statement layout. Negatives render parenthesized (credits).
export const formatAmount = (value: number): string => {
  const formatted = Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return value < 0 ? `(${formatted})` : formatted
}

// Fee derivation for the summary: PEPM × billable, for the funded (billed) month.
// A funding invoice bills one month at a time (see the source spreadsheet), so the
// fee is a single month of PEPM × billable. Returns null when there is no real
// PEPM (skips the "—" noise on non-billable rows).
export const formatFeeCalc = (
  pepmCents: number | null,
  billable: number
): string | null => {
  if (typeof pepmCents !== 'number' || !Number.isFinite(pepmCents)) return null
  return `$${formatAmount(pepmCents / 100)} × ${billable}`
}

// Benefit IDs are long UUIDs; show first4…last4 and copy the full value on click.
export const truncateId = (id: string): string =>
  id.length > 8 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id
